<template>
  <div v-if="isVisible" class="workspace-role-rotator" :class="{ collapsed: !isExpanded }">
    <div
      class="rotator-header"
      @click="isExpanded = !isExpanded"
      :title="isExpanded ? 'Collapse' : 'Expand workspace role rotator'"
    >
      <span class="rotator-icon"><RefreshCw :size="14" /></span>
      <span v-if="isExpanded" class="rotator-label">Demo Role</span>
      <span class="role-chip">{{ roleLabel(currentRole) }}</span>
    </div>

    <div v-if="isExpanded" class="rotator-body">
      <p class="rotator-hint">
        Writes your real user-doc role and JWT. Smoke tests use the JWT after reload — not the
        client-only preview switcher. Caseload / class / aide roles need seed assignments.
      </p>
      <p class="jwt-line">
        Store: {{ roleLabel(currentRole) }} · JWT: {{ roleLabel(jwtRole) || jwtRole || '…' }}
      </p>

      <div class="role-buttons">
        <button
          v-for="opt in WORKSPACE_ROTATOR_ROLES"
          :key="opt.value"
          type="button"
          class="role-btn"
          :class="{ active: currentRole === opt.value }"
          :disabled="busy || currentRole === opt.value"
          @click="rotateTo(opt.value)"
        >
          {{ opt.label }}
        </button>
      </div>

      <p v-if="statusMessage" class="status-msg" :class="{ error: isError }">
        {{ statusMessage }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/firebase'
import { useAuthStore } from '@/store/authStore'
import { useDebugToolsSettings } from '@/composables/useDebugToolsSettings'
import { WORKSPACE_ROTATOR_ROLES, isWorkspaceRotatorEmail } from '@/utils/workspaceRoleRotator'
import {
  clearDebugUserSpoof,
  loadSmokeAuthInspection,
  waitForJwtRoleClaim
} from './smoke-test/authContext.js'
import { RefreshCw } from 'lucide-vue-next'

const authStore = useAuthStore()
const isExpanded = ref(true)
const busy = ref(false)
const statusMessage = ref('')
const isError = ref(false)

const currentUser = computed(() => authStore.currentUser)
const currentRole = computed(() => currentUser.value?.role || '')
const jwtRole = ref(null)

async function refreshJwtRole() {
  try {
    const loaded = await loadSmokeAuthInspection(currentUser.value)
    jwtRole.value = loaded.inspection.jwtRole
  } catch {
    jwtRole.value = null
  }
}

const { roleSwitcherVisible } = useDebugToolsSettings()

const isVisible = computed(
  () =>
    roleSwitcherVisible.value &&
    !!currentUser.value?.email &&
    isWorkspaceRotatorEmail(currentUser.value.email)
)

function roleLabel(role) {
  return WORKSPACE_ROTATOR_ROLES.find((r) => r.value === role)?.label || role || '—'
}

async function rotateTo(role) {
  if (!role || busy.value) return
  busy.value = true
  isError.value = false
  statusMessage.value = `Switching to ${roleLabel(role)}…`

  try {
    const fn = httpsCallable(functions, 'rotateWorkspaceDemoRole')
    const { data } = await fn({ role })

    if (data?.unchanged) {
      statusMessage.value = `Already ${roleLabel(role)}`
      busy.value = false
      return
    }

    clearDebugUserSpoof()
    authStore.mergeCurrentUser({ role })
    statusMessage.value = `Role set to ${roleLabel(role)}. Waiting for JWT claim…`

    // syncUserClaims runs on the Firestore write. Wait until the token
    // actually carries the new role so the smoke runner cannot run as the
    // previous JWT after a too-fast reload.
    await authStore.refreshToken()
    const claimed = await waitForJwtRoleClaim(role)
    statusMessage.value = claimed ? 'JWT updated. Reloading…' : 'JWT still catching up. Reloading…'
    window.location.reload()
  } catch (err) {
    console.error('Workspace role rotation failed:', err)
    isError.value = true
    statusMessage.value = err?.message || 'Failed to rotate role'
    busy.value = false
  }
}

onMounted(refreshJwtRole)
</script>

<style scoped>
.workspace-role-rotator {
  position: fixed;
  top: 72px;
  right: 16px;
  z-index: 9997;
  width: 280px;
  background: #1e293b;
  color: #f1f5f9;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.28);
  font-size: 13px;
  overflow: hidden;
}

.workspace-role-rotator.collapsed {
  width: auto;
}

.rotator-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  user-select: none;
  background: #0f172a;
}

.rotator-icon {
  display: flex;
  opacity: 0.85;
}

.rotator-label {
  font-weight: 600;
  flex: 1;
}

.role-chip {
  background: #334155;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.rotator-body {
  padding: 10px 12px 12px;
}

.rotator-hint {
  margin: 0 0 8px;
  color: #94a3b8;
  font-size: 11px;
  line-height: 1.4;
}

.jwt-line {
  margin: 0 0 10px;
  color: #e2e8f0;
  font-size: 11px;
}

.role-buttons {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: min(420px, calc(100vh - 200px));
  overflow-y: auto;
}

.role-btn {
  appearance: none;
  border: 1px solid #475569;
  background: #334155;
  color: #f8fafc;
  border-radius: 6px;
  padding: 8px 10px;
  text-align: left;
  cursor: pointer;
  font-size: 13px;
}

.role-btn:hover:not(:disabled) {
  background: #475569;
}

.role-btn.active {
  border-color: #38bdf8;
  background: #0c4a6e;
}

.role-btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.status-msg {
  margin: 10px 0 0;
  font-size: 11px;
  color: #7dd3fc;
}

.status-msg.error {
  color: #fca5a5;
}
</style>
